//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer do Portal LiveModa (portal-b1-moda-front,
// page-specific-components/smartflow/SmartFlowImporterForm.js) para a plataforma SmartFlow. Este
// arquivo concentra a LÓGICA PURA (formatação, comparação com a Receita, montagem/validação do
// payload do SAP) — portada ~1:1 do portal, sem dependência de UI. A tela em si
// (SmartFlowImporterForm.js) foi reescrita em reactstrap (o portal usava Tailwind + primitivos
// próprios).
//
// Decisões de negócio confirmadas ao vivo no portal (mantidas):
//  - Endereço vem SEMPRE do XML da NF-e (não da Receita) — matriz vs. filial divergem demais.
//  - CardType por papel (cSupplier/cCustomer); GroupCode por papel.
//  - CardCode: reaproveita o já gravado no SAP; senão sequência configurada; senão CNPJ/idEstrangeiro.
//  - Parceiro estrangeiro: sem CNPJ/CPF, usa <idEstrangeiro> (TaxId5 no SAP).
//======
import { resolverCodigoPaisSap } from "services/smartflow/paisesSap"

export function formatDataEmissao(value) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return value.length > 10 ? date.toLocaleString("pt-BR") : date.toLocaleDateString("pt-BR")
}

export function formatCnpjCpf(value, idEstrangeiro) {
  if (!value) return idEstrangeiro ? `Estrangeiro: ${idEstrangeiro}` : "—"
  const digits = String(value).replace(/\D/g, "")
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
  }
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
  }
  return value
}

export function formatValor(value) {
  if (value === null || value === undefined) return "—"
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

export function formatEndereco(receita) {
  if (!receita) return "—"
  const partes = [
    [receita.logradouro, receita.numero].filter(Boolean).join(", "),
    receita.complemento,
    receita.bairro,
    receita.municipio && receita.uf ? `${receita.municipio}/${receita.uf}` : receita.municipio,
    receita.cep,
  ].filter(Boolean)
  return partes.length ? partes.join(" — ") : "—"
}

export function normalizarTexto(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[.,\-/]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ")
}

export function resolverCountryCode(nomePais) {
  return resolverCodigoPaisSap(nomePais, normalizarTexto) || "BR"
}

export function compararComReceita(nota, receita) {
  const razaoBate =
    !!nota.razaoSocial &&
    !!receita?.razao_social &&
    normalizarTexto(nota.razaoSocial) === normalizarTexto(receita.razao_social)
  const situacaoAtiva = normalizarTexto(receita?.descricao_situacao_cadastral) === "ATIVA"

  const detalhes = []
  if (!razaoBate) {
    detalhes.push(
      `Razão social do XML ("${nota.razaoSocial || "—"}") diverge da Receita ("${
        receita?.razao_social || "—"
      }").`
    )
  }
  if (!situacaoAtiva) {
    detalhes.push(
      `Situação cadastral na Receita: ${
        receita?.descricao_situacao_cadastral || "não informada"
      } (esperado ATIVA).`
    )
  }

  return { razaoBate, situacaoAtiva, ok: razaoBate && situacaoAtiva, detalhe: detalhes.join(" ") }
}

const TIPOS_LOGRADOURO_CONHECIDOS = {
  AV: "Avenida", AVENIDA: "Avenida", R: "Rua", RUA: "Rua", AL: "Alameda", ALAMEDA: "Alameda",
  TRAV: "Travessa", TRAVESSA: "Travessa", ROD: "Rodovia", RODOVIA: "Rodovia", ESTR: "Estrada",
  ESTRADA: "Estrada", PCA: "Praça", PRACA: "Praça", LRG: "Largo", LARGO: "Largo", VL: "Vila",
  VILA: "Vila", JD: "Jardim", JARDIM: "Jardim", QD: "Quadra", QUADRA: "Quadra", VD: "Viaduto",
  VIADUTO: "Viaduto",
}

export function extrairTipoLogradouro(enderecoCompleto) {
  const texto = String(enderecoCompleto || "").trim()
  const match = texto.match(/^([A-Za-zÀ-ÿ]+)\.?\s+(.+)$/)
  if (!match) return { tipo: "", logradouro: texto }

  const tipoEncontrado = TIPOS_LOGRADOURO_CONHECIDOS[normalizarTexto(match[1])]
  if (!tipoEncontrado) return { tipo: "", logradouro: texto }

  return { tipo: tipoEncontrado, logradouro: match[2].trim() }
}

//====== Monta 1 linha no formato SAP (BusinessPartners) a partir da nota (XML + Receita).
// `tipoOrigem` decide CardType/GroupCode; endereço sempre do XML.
export function montarParceiroSap(row, cardCode, jaGravadoNoSap, usouSequencia, tipoOrigem, grupoCode) {
  const cnpjDigits = String(row.cnpjCpf || "").replace(/\D/g, "")
  const idEstrangeiro = String(row.idEstrangeiro || row.TaxIdEstrangeiro || "").trim()
  const situacaoAtiva = row.receita
    ? normalizarTexto(row.receita.descricao_situacao_cadastral) === "ATIVA"
    : true
  const enderecoPreferido = row.endereco?.logradouro ? row.endereco : row.receita
  const enderecoVemDoXml = enderecoPreferido === row.endereco

  const logradouroExtraido = extrairTipoLogradouro(enderecoPreferido?.logradouro)
  const tipoLogradouroConfiavel = enderecoVemDoXml ? "" : row.receita?.descricao_tipo_de_logradouro

  return {
    fileId: row.fileId,
    numeroNF: row.numeroNF,
    fileName: row.fileName,
    jaGravadoNoSap: !!jaGravadoNoSap,
    tipo: tipoOrigem === "cliente" ? "cliente" : "fornecedor",
    usouSequencia: !!usouSequencia,
    CardCode: cardCode || cnpjDigits || idEstrangeiro,
    CardType: tipoOrigem === "cliente" ? "cCustomer" : "cSupplier",
    GroupCode: grupoCode || undefined,
    CardName: row.receita?.razao_social || row.razaoSocial || "",
    CardForeignName: row.receita?.nome_fantasia || row.nomeFantasia || "",
    FederalTaxID: cnpjDigits,
    Valid: situacaoAtiva ? "tYES" : "tNO",
    InscricaoEstadual: row.inscricaoEstadual || "",
    Email: row.receita?.email || "",
    Telefone: row.receita?.ddd_telefone_1 || row.endereco?.telefone || "",
    AddressType: "bo_BillTo",
    Country: resolverCountryCode(row.endereco?.pais),
    TipoLogradouro: tipoLogradouroConfiavel || logradouroExtraido.tipo || "",
    Street: tipoLogradouroConfiavel
      ? enderecoPreferido?.logradouro || ""
      : logradouroExtraido.logradouro || "",
    StreetNo: enderecoPreferido?.numero || "",
    Complemento: enderecoPreferido?.complemento || "",
    Block: enderecoPreferido?.bairro || "",
    City: enderecoPreferido?.municipio || "",
    County: enderecoPreferido?.uf || "",
    ZipCode: enderecoPreferido?.cep ? String(enderecoPreferido.cep).replace(/\D/g, "") : "",
    CodigoMunicipioIbge: enderecoVemDoXml
      ? row.endereco?.codigoMunicipioIbge || null
      : row.receita?.codigo_municipio_ibge || null,
    TaxIdEstrangeiro: idEstrangeiro,
  }
}

//====== Simula as validações da Service Layer antes de deixar marcar/gravar uma linha.
export function validarParceiroSap(row, contagemCardCodeNoLote) {
  const erros = []
  const cardCode = (row.CardCode || "").trim()
  const cardName = (row.CardName || "").trim()
  const documento = String(row.FederalTaxID || "").replace(/\D/g, "")
  const idEstrangeiro = String(row.TaxIdEstrangeiro || "").trim()

  if (!cardCode) {
    erros.push("CardCode é obrigatório.")
  } else {
    if (cardCode.length > 15) erros.push("CardCode excede 15 caracteres (limite do SAP).")
    if ((contagemCardCodeNoLote.get(cardCode) || 0) > 1) {
      erros.push("CardCode duplicado no lote — é chave primária, não pode repetir.")
    }
  }

  if (!cardName) {
    erros.push("CardName (Razão Social) é obrigatório.")
  } else if (cardName.length > 100) {
    erros.push("CardName excede 100 caracteres (limite do SAP).")
  }

  if ((row.CardForeignName || "").length > 100) {
    erros.push("CardForeignName excede 100 caracteres (limite do SAP).")
  }

  if (!["tYES", "tNO"].includes(row.Valid)) {
    erros.push("Valid precisa ser tYES ou tNO.")
  }

  if (!documento && !idEstrangeiro) {
    erros.push("FederalTaxID (CNPJ/CPF) ou ID de estrangeiro é obrigatório.")
  } else if (documento) {
    if (documento.length !== 11 && documento.length !== 14) {
      erros.push("FederalTaxID precisa ter 11 (CPF) ou 14 (CNPJ) dígitos.")
    } else if (documento.length > 32) {
      erros.push("FederalTaxID excede 32 caracteres (limite do SAP).")
    }
  }

  if (idEstrangeiro.length > 32) {
    erros.push("ID de estrangeiro excede 32 caracteres (limite do SAP, TaxId5).")
  }

  if ((row.ZipCode || "").length > 20) {
    erros.push("ZipCode excede 20 caracteres (limite do SAP).")
  }

  return { ok: erros.length === 0, erros }
}
